import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Store } from '@/services/stores'
import type { CartItem } from '@/core/cart'
import { resolveRefStorefrontNavLink } from '@/storefront-runtime/lib/resolveRefStorefrontNavLink'
import { mergeCartLines, sanitizeCartItems } from '@/core/cart'
import { buyerSupabase } from '@/lib/buyerSupabase'

export interface BuyerProfile {
  id: string
  email: string
  name: string | null
  phone: string | null
}

const STORAGE_PREFIX = 'ref-sf-cart:'

function storageKey(storeId: string): string {
  return `${STORAGE_PREFIX}${storeId}`
}

function loadCart(storeId: string): CartItem[] {
  try {
    const raw = localStorage.getItem(storageKey(storeId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    const sanitized = sanitizeCartItems(parsed)
    return sanitized
  } catch {
    return []
  }
}

function saveCart(storeId: string, items: CartItem[]): void {
  try {
    localStorage.setItem(storageKey(storeId), JSON.stringify(mergeCartLines(items)))
  } catch {
    /* ignore */
  }
}

export interface ReferenceStoreContextValue {
  store: Store
  storeSlug: string
  /**
   * When set (e.g. `/preview-template/my-id`), all storefront URLs are rooted here
   * so `/ref/:slug/...` becomes `/preview-template/my-id/ref/:slug/...` (single BrowserRouter).
   */
  storefrontPathPrefix: string
  cart: CartItem[]
  setCart: (next: CartItem[] | ((prev: CartItem[]) => CartItem[])) => void
  cartCount: number
  cartDrawerOpen: boolean
  openCartDrawer: () => void
  closeCartDrawer: () => void
  /** Currently authenticated buyer (null = guest or seller) */
  buyer: BuyerProfile | null
  buyerLoading: boolean
  refreshBuyer: () => Promise<void>
  signOutBuyer: () => Promise<void>
}

const ReferenceStoreContext = createContext<ReferenceStoreContextValue | null>(null)

export function ReferenceStoreProvider({
  store,
  storeSlug,
  storefrontPathPrefix = '',
  children,
}: {
  store: Store
  storeSlug: string
  /** Empty in production; set for template preview nested URLs. */
  storefrontPathPrefix?: string
  children: ReactNode
}) {
  const [cart, setCartState] = useState<CartItem[]>([])
  const [hydrated, setHydrated] = useState(false)
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false)
  const [buyer, setBuyer] = useState<BuyerProfile | null>(null)
  const [buyerLoading, setBuyerLoading] = useState(true)

  useEffect(() => {
    const loaded = loadCart(store.id)
    setCartState(loaded)
    saveCart(store.id, loaded)
    setHydrated(true)
  }, [store.id])

  /**
   * Email attribution: track-email-click redirects with ?fs_et= (JWT typ email_attr) once.
   * Persist for checkout, then strip from the address bar so the token is not visible long-term.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const u = new URL(window.location.href)
      const t = u.searchParams.get('fs_et')
      if (t && t.trim() !== '') {
        sessionStorage.setItem('fs_email_attribution', t.trim())
        u.searchParams.delete('fs_et')
        const next = `${u.pathname}${u.search}${u.hash}`
        window.history.replaceState(null, '', next)
      }
    } catch {
      /* ignore */
    }
  }, [])

  const resolveBuyer = useCallback(async () => {
    setBuyerLoading(true)
    try {
      const { data: { user } } = await buyerSupabase.auth.getUser()
      if (user && user.user_metadata?.role === 'buyer') {
        setBuyer({
          id: user.id,
          email: user.email ?? '',
          name: user.user_metadata?.name ?? null,
          phone: user.user_metadata?.phone ?? null,
        })
      } else {
        setBuyer(null)
      }
    } catch {
      setBuyer(null)
    } finally {
      setBuyerLoading(false)
    }
  }, [])

  useEffect(() => {
    void resolveBuyer()
    const { data: { subscription } } = buyerSupabase.auth.onAuthStateChange(() => {
      void resolveBuyer()
    })
    return () => subscription.unsubscribe()
  }, [resolveBuyer])

  const signOutBuyer = useCallback(async () => {
    await buyerSupabase.auth.signOut()
    setBuyer(null)
  }, [])

  const setCart = useCallback(
    (next: CartItem[] | ((prev: CartItem[]) => CartItem[])) => {
      setCartState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next
        const merged = mergeCartLines(resolved)
        if (hydrated) saveCart(store.id, merged)
        return merged
      })
    },
    [store.id, hydrated]
  )

  const cartCount = useMemo(
    () => cart.reduce((a, l) => a + l.quantity, 0),
    [cart]
  )

  const openCartDrawer = useCallback(() => setCartDrawerOpen(true), [])
  const closeCartDrawer = useCallback(() => setCartDrawerOpen(false), [])

  const value = useMemo(
    (): ReferenceStoreContextValue => ({
      store,
      storeSlug,
      storefrontPathPrefix,
      cart,
      setCart,
      cartCount,
      cartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      buyer,
      buyerLoading,
      refreshBuyer: resolveBuyer,
      signOutBuyer,
    }),
    [store, storeSlug, storefrontPathPrefix, cart, setCart, cartCount, cartDrawerOpen, openCartDrawer, closeCartDrawer, buyer, buyerLoading, resolveBuyer, signOutBuyer]
  )

  return (
    <ReferenceStoreContext.Provider value={value}>{children}</ReferenceStoreContext.Provider>
  )
}

export function useReferenceStore(): ReferenceStoreContextValue {
  const ctx = useContext(ReferenceStoreContext)
  if (!ctx) {
    throw new Error('useReferenceStore must be used within ReferenceStoreProvider')
  }
  return ctx
}

/** Same context as `useReferenceStore` but returns null outside the buyer storefront tree. */
export function useOptionalReferenceStore(): ReferenceStoreContextValue | null {
  return useContext(ReferenceStoreContext)
}

/** Resolves layout/header/footer links to the correct path (includes template preview prefix when set). */
export function useResolveRefStorefrontNavLink(): (link: unknown) => string {
  const { storeSlug, storefrontPathPrefix } = useReferenceStore()
  return useCallback(
    (link: unknown) => resolveRefStorefrontNavLink(link, storeSlug, storefrontPathPrefix),
    [storeSlug, storefrontPathPrefix]
  )
}
